import { Controller } from "@hotwired/stimulus";

// A reconnected Cable subscription does not replay replies it missed. Recover
// just the transcript; replacing the whole frame would erase unsent text/files.
export default class extends Controller {
  static targets = ["messages"];
  static values = { url: String };

  connect() {
    this.active = true;
    this.onResume = () => this.sync();
    document.addEventListener("visibilitychange", this.onResume);
    window.addEventListener("online", this.onResume);
    window.addEventListener("kit:opened", this.onResume);
    const source = this.element.querySelector("turbo-cable-stream-source");
    this.observer = new MutationObserver(() => {
      if (source.hasAttribute("connected")) this.sync();
    });
    if (source)
      this.observer.observe(source, {
        attributes: true,
        attributeFilter: ["connected"],
      });
    // Also cover a missed individual broadcast while the socket stays open.
    this.timer = setInterval(this.onResume, 30000);
    this.sync();
  }

  disconnect() {
    this.active = false;
    this.request?.abort();
    this.observer.disconnect();
    clearInterval(this.timer);
    document.removeEventListener("visibilitychange", this.onResume);
    window.removeEventListener("online", this.onResume);
    window.removeEventListener("kit:opened", this.onResume);
  }

  sync() {
    if (this.pending) return this.pending;
    const panel = this.element.closest("#kit-chat-panel");
    if (
      !this.active ||
      document.hidden ||
      (panel && !panel.classList.contains("kit-chat-panel--open"))
    )
      return;
    this.request = new AbortController();
    this.pending = this.recover(this.request.signal)
      .catch(() => {
        // Offline: the next reconnect, reopen, or heartbeat retries.
      })
      .finally(() => {
        this.pending = null;
      });
    return this.pending;
  }

  async recover(signal) {
    const response = await fetch(this.urlValue, {
      credentials: "same-origin",
      cache: "no-store",
      signal,
      headers: this.etag ? { "If-None-Match": this.etag } : {},
    });
    if (!response.ok) return;
    const html = await response.text();
    if (!this.active || signal.aborted) return;
    const snapshot = new DOMParser().parseFromString(html, "text/html");
    const messages = this.messagesTarget;
    for (const node of Array.from(snapshot.body.children)) {
      const id = Number(node.id.match(/^message_(\d+)$/)?.[1]);
      if (!id) continue;
      const existing = messages.querySelector(`#${node.id}`);
      if (existing) {
        // Recover a dropped final replacement, but never overwrite a finished
        // live answer with an older snapshot, or disturb its feedback controls.
        if (existing.dataset.kitStreaming === "true")
          messages.replaceChild(node, existing);
        continue;
      }
      const next = Array.from(messages.children).find(
        (child) => Number(child.id.match(/^message_(\d+)$/)?.[1]) > id
      );
      messages.insertBefore(node, next || null);
      messages.querySelector("#chat_empty_state")?.remove();
    }
    this.etag = response.headers.get("ETag");
  }
}
